/**
 * JourneyPath - Visual progress indicator for K students
 *
 * Shows the full K journey with:
 * - PreK grade indicator (completed) on the left
 * - 7 K track stops in the middle with completed/current/locked states
 * - Grade 1 indicator (next) on the right
 * - Today's XP display
 *
 * Grade indicators are rectangular badges, track stops are circles.
 */

import React, { useState, useEffect } from 'react';
import { Check, ChevronRight, Lock, Star } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import { getProgression } from '../../../config/api';

// Track order for K journey - icons that visually represent each track
const K_TRACK_ORDER = [
  { id: 'TRACK_K_NAMING', label: 'Numbers', icon: '123', iconClass: 'text-sm' },
  { id: 'TRACK_K_SUBITIZING', label: 'Dots', icon: '••••', iconClass: 'text-sm' },
  { id: 'TRACK_K_COMPARING', label: 'Compare', icon: '< >', iconClass: 'text-sm' },
  { id: 'TRACK_K_PARTWHOLE', label: 'Parts', icon: '◐', iconClass: 'text-lg' },
  { id: 'TRACK_K_ADDITION_WITHIN_5', label: 'Add 5', icon: '+', iconClass: 'text-lg' },
  { id: 'TRACK_K_ACCURACY', label: 'Accuracy', icon: '◎', iconClass: 'text-lg' },
  { id: 'TRACK13', label: 'Add 10', icon: '++', iconClass: 'text-sm' },
] as const;

type TrackState = 'completed' | 'current' | 'locked';

interface JourneyPathProps {
  currentTrack?: string; // user.focusTrack
}

/**
 * Determine the state of a track based on its position relative to current track
 */
function getTrackState(trackId: string, currentTrack: string | undefined): TrackState {
  const trackIndex = K_TRACK_ORDER.findIndex(t => t.id === trackId);
  const currentIndex = K_TRACK_ORDER.findIndex(t => t.id === currentTrack);

  // If no current track found, default to first track as current
  const effectiveCurrentIndex = currentIndex === -1 ? 0 : currentIndex;

  if (trackIndex < effectiveCurrentIndex) {
    return 'completed';
  } else if (trackIndex === effectiveCurrentIndex) {
    return 'current';
  } else {
    return 'locked';
  }
}

/**
 * Individual track stop component
 */
const TrackStop: React.FC<{
  track: typeof K_TRACK_ORDER[number];
  state: TrackState;
}> = ({ track, state }) => {
  return (
    <div className="flex flex-col items-center">
      {/* Current indicator - small dot */}
      {state === 'current' && (
        <div className="w-1.5 h-1.5 rounded-full bg-orange-400 mb-1.5" />
      )}
      {state !== 'current' && <div className="h-[12px]" />}

      {/* Track stop */}
      <div
        className={`
          relative w-10 h-10 rounded-full flex items-center justify-center
          ${state === 'completed'
            ? 'bg-teal-400 shadow-sm'
            : state === 'current'
            ? 'bg-white shadow-md ring-2 ring-orange-300'
            : 'bg-gray-100 border-2 border-dashed border-gray-300'
          }
        `}
      >
        {state === 'completed' ? (
          <Check className="w-5 h-5 text-white" strokeWidth={3} />
        ) : state === 'locked' ? (
          <Lock className="w-4 h-4 text-gray-300" strokeWidth={2} />
        ) : (
          <span className={`${track.iconClass} font-semibold text-orange-500`}>
            {track.icon}
          </span>
        )}
      </div>

      {/* Track label */}
      <span
        className={`
          mt-1.5 text-xs font-medium
          ${state === 'completed'
            ? 'text-teal-600'
            : state === 'current'
            ? 'text-orange-500'
            : 'text-gray-400'
          }
        `}
      >
        {track.label}
      </span>
    </div>
  );
};

/**
 * Arrow connector between stops
 */
const ArrowConnector: React.FC<{ completed: boolean }> = ({ completed }) => {
  return (
    <div className="flex items-center h-10 mx-1.5 mt-[12px]">
      <ChevronRight
        className={`w-4 h-4 ${completed ? 'text-teal-300' : 'text-gray-200'}`}
        strokeWidth={2}
      />
    </div>
  );
};

/**
 * Grade level indicator - visually distinct from track stops
 */
const GradeIndicator: React.FC<{
  label: string;
  state: 'completed' | 'next';
}> = ({ label, state }) => {
  return (
    <div className="flex flex-col items-center">
      <div className="h-[12px]" />
      <div
        className={`
          px-3 py-2 rounded-lg flex items-center justify-center gap-1.5
          ${state === 'completed'
            ? 'bg-teal-100 border-2 border-teal-400'
            : 'bg-gray-50 border-2 border-dashed border-gray-300'
          }
        `}
      >
        {state === 'completed' && (
          <Check className="w-4 h-4 text-teal-500" strokeWidth={3} />
        )}
        {state === 'next' && (
          <Lock className="w-3.5 h-3.5 text-gray-400" strokeWidth={2} />
        )}
        <span
          className={`text-sm font-bold ${
            state === 'completed' ? 'text-teal-600' : 'text-gray-400'
          }`}
        >
          {label}
        </span>
      </div>
      <span
        className={`mt-1.5 text-xs font-medium ${
          state === 'completed' ? 'text-teal-600' : 'text-gray-400'
        }`}
      >
        {state === 'completed' ? 'Complete' : 'Next'}
      </span>
    </div>
  );
};

/**
 * Today's XP display component - child-friendly design
 * Shows completion state when daily target (10 XP) is reached
 */
const TodayXpDisplay: React.FC<{ todayXp: number | null }> = ({ todayXp }) => {
  // Show loading skeleton while fetching
  if (todayXp === null) {
    return (
      <div className="flex items-center gap-1.5 bg-yellow-100 px-3 py-1.5 rounded-full border-2 border-yellow-300 shadow-sm animate-pulse">
        <Star className="w-5 h-5 text-yellow-300" />
        <div className="h-4 w-12 bg-yellow-200 rounded" />
      </div>
    );
  }

  const isComplete = todayXp >= 10;

  if (isComplete) {
    return (
      <div className="flex items-center gap-1.5 bg-green-100 px-3 py-1.5 rounded-full border-2 border-green-400 shadow-sm">
        <Check className="w-5 h-5 text-green-600" strokeWidth={3} />
        <span className="text-sm font-bold text-green-700">
          {todayXp.toFixed(1)} XP
        </span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5 bg-yellow-100 px-3 py-1.5 rounded-full border-2 border-yellow-300 shadow-sm">
      <Star className="w-5 h-5 text-yellow-500 fill-yellow-400" />
      <span className="text-sm font-bold text-yellow-700">
        {todayXp.toFixed(1)} XP
      </span>
    </div>
  );
};

/**
 * Main JourneyPath component for K grade
 */
const JourneyPath: React.FC<JourneyPathProps> = ({ currentTrack }) => {
  const { user } = useAuth();
  const [todayXp, setTodayXp] = useState<number | null>(null);

  useEffect(() => {
    const fetchTodayXp = async () => {
      if (!user?.userId) return;

      try {
        const progression = await getProgression(user.userId);
        setTodayXp(progression.todayXp);
      } catch (error) {
        console.error('Failed to fetch today\'s XP:', error);
      }
    };

    fetchTodayXp();
  }, [user?.userId]);

  return (
    <div className="flex flex-col items-center gap-3 py-2 px-4">
      {/* Today's XP */}
      <TodayXpDisplay todayXp={todayXp} />

      {/* Track progression */}
      <div className="flex items-start">
        {/* Previous grade: PreK (completed) */}
        <GradeIndicator label="PreK" state="completed" />
        <ArrowConnector completed={true} />

        {/* Current grade: K tracks */}
        {K_TRACK_ORDER.map((track, index) => {
          const state = getTrackState(track.id, currentTrack);
          const isLast = index === K_TRACK_ORDER.length - 1;

          return (
            <React.Fragment key={track.id}>
              <TrackStop track={track} state={state} />
              {!isLast && <ArrowConnector completed={state === 'completed'} />}
            </React.Fragment>
          );
        })}

        {/* Next grade: Grade 1 (locked) */}
        <ArrowConnector completed={false} />
        <GradeIndicator label="Grade 1" state="next" />
      </div>
    </div>
  );
};

export default JourneyPath;
